import { SAMPLE_IMAGE_URLS } from "../ui/components/AiImageGallery";

type ImageType = "ai" | "community" | "sample";

type ConvertImageUrlToFileParams = {
  imageUrl: string;
  imageName?: string;
  imageId?: number;
  imageType?: ImageType;
};

/**
 * Convert an image URL to a File object for uploading to the design lab
 * Handles AI-generated images, community images, and sample images
 */
export const convertImageUrlToFile = async ({
  imageUrl,
  imageName,
  imageId,
  imageType = "ai"
}: ConvertImageUrlToFileParams): Promise<File> => {
  // IMPORTANT: do not alter query params on a presigned S3 URL.
  // Ask browser not to use cache, but keep the URL unchanged.
  const imageResponse = await fetch(imageUrl, {
    mode: "cors",
    credentials: "omit",
    cache: "reload"
  });

  if (!imageResponse.ok) {
    throw new Error(`Failed to download image: ${imageResponse.status}`);
  }

  const imageBlob = await imageResponse.blob();
  const blobType = imageBlob.type || (imageType === "community" ? "image/jpeg" : "image/png");

  // Determine file name based on image type
  let fileName: string;

  if (imageType === "community") {
    // Determine file extension from blob type for community images
    let extension = "jpg";
    if (blobType.includes("png")) {
      extension = "png";
    } else if (blobType.includes("webp")) {
      extension = "webp";
    } else if (blobType.includes("gif")) {
      extension = "gif";
    }

    fileName = imageName
      ? `community-${imageName.replace(/[^a-zA-Z0-9]/g, "-")}.${extension}`
      : `community-image-${Date.now()}.${extension}`;
  } else {
    // Check if it's a sample image
    const isSampleImage = SAMPLE_IMAGE_URLS.includes(imageUrl);
    const prefix = isSampleImage ? "sample-image" : "ai-generated";
    const id = imageId ?? Date.now();
    fileName = `${prefix}-${id}.png`;
  }

  return new File([imageBlob], fileName, {
    type: blobType
  });
};

type UploadImageParams = {
  imageUrl: string;
  uploadArtwork: (file: File) => Promise<void>;
  imageName?: string;
  imageId?: number;
  imageType?: ImageType;
};

/**
 * Upload an image to the design lab using the existing artwork upload flow
 * Supports AI-generated images, community images, and sample images
 */
export const uploadImageToDesignLab = async ({
  imageUrl,
  uploadArtwork,
  imageName,
  imageId,
  imageType = "ai"
}: UploadImageParams): Promise<void> => {
  try {
    // Convert the image URL to a File object
    const params: ConvertImageUrlToFileParams = { imageUrl, imageType };
    if (imageName !== undefined) {
      params.imageName = imageName;
    }
    if (imageId !== undefined) {
      params.imageId = imageId;
    }
    const file = await convertImageUrlToFile(params);

    await uploadArtwork(file);
  } catch (error) {
    console.error(`Failed to upload ${imageType} image to design lab:`, error);
    window.Honeybadger?.notify(`Failed to upload ${imageType} image to design lab`, {
      params: {
        error: error instanceof Error ? error.message : String(error),
        error_object: error instanceof Error ? error.toString() : String(error),
        stack_trace: error instanceof Error ? error.stack : null,
        service: "imageUploadService",
        imageType,
        imageUrl
      }
    });
    throw new Error(`Failed to upload ${imageType} image. Please try again.`);
  }
};

// Legacy exports for backward compatibility
export const convertAiImageUrlToFile = async (imageUrl: string, imageId: number): Promise<File> =>
  convertImageUrlToFile({
    imageUrl,
    imageId,
    imageType: "ai"
  });

export const uploadAiImageToDesignLab = async ({
  imageUrl,
  imageId,
  uploadArtwork
}: {
  imageUrl: string;
  imageId: number;
  uploadArtwork: (file: File) => Promise<void>;
}): Promise<void> =>
  uploadImageToDesignLab({
    imageUrl,
    imageId,
    uploadArtwork,
    imageType: "ai"
  });

export const convertCommunityImageUrlToFile = async (
  imageUrl: string,
  imageName: string
): Promise<File> =>
  convertImageUrlToFile({
    imageUrl,
    imageName,
    imageType: "community"
  });

export const uploadCommunityImageToDesignLab = async ({
  imageUrl,
  imageName,
  uploadArtwork
}: {
  imageUrl: string;
  imageName: string;
  uploadArtwork: (file: File) => Promise<void>;
}): Promise<void> =>
  uploadImageToDesignLab({
    imageUrl,
    imageName,
    uploadArtwork,
    imageType: "community"
  });
